import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Swords, Timer, Trophy } from 'lucide-react';
import { useAuth } from '../lib/auth-context';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { matchmakeBattle } from '../lib/battle-client';

export default function BattleLobby() {
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const [isMatching, setIsMatching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleMatchmake = async () => {
    if (!accessToken) return;
    setIsMatching(true);
    setError(null);
    try {
      const result = await matchmakeBattle(accessToken);
      navigate(`/battle/${result.room.id}`);
    } catch (e: any) {
      setError(e?.message || 'Could not start battle search');
    } finally {
      setIsMatching(false);
    }
  };

  return (
    <div className="min-h-screen tension-page-bg px-4 py-8">
      <div className="max-w-3xl mx-auto space-y-4">
        <Card className="p-6 bg-gradient-to-br from-violet-600 via-fuchsia-600 to-pink-600 text-white border-0">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-wider text-white/80 mb-2">Phase 2 Live Battles</p>
              <h1 className="text-2xl md:text-3xl font-bold">1v1 Stream Battle Lobby</h1>
              <p className="mt-2 text-white/90 text-sm max-w-xl">
                Match instantly, stream live, send score-driving gifts, and finish with a server-authoritative winner.
              </p>
            </div>
            <Badge className="bg-white/20 text-white border-white/30">Beta rollout</Badge>
          </div>
          <div className="mt-4 flex flex-wrap gap-2 text-xs">
            <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-3 py-1">
              <Timer className="w-3.5 h-3.5" /> 3-minute rounds
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-3 py-1">
              <Trophy className="w-3.5 h-3.5" /> Live score + gifts
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-3 py-1">
              <Swords className="w-3.5 h-3.5" /> Matchmaking enabled
            </span>
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Ready to challenge someone?</h2>
          <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">
            You will either join an available waiting battler or open a new room while waiting for a challenger.
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <Button onClick={() => void handleMatchmake()} disabled={!accessToken || isMatching}>
              {isMatching ? 'Finding opponent...' : 'Start matchmaking'}
            </Button>
            <Button variant="outline" onClick={() => navigate('/community')}>
              Back to Community
            </Button>
          </div>
          {!accessToken && (
            <p className="text-xs text-amber-600 dark:text-amber-300 mt-3">
              Log in to enter live battles.
            </p>
          )}
          {error && (
            <p className="text-xs text-red-600 dark:text-red-300 mt-3">
              {error}
            </p>
          )}
        </Card>
      </div>
    </div>
  );
}
